import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import {
  Container,
  Title,
  TextInput,
  NumberInput,
  Textarea,
  Switch,
  Button,
  Stack,
  Group,
  Select,
  Tabs,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { RichTextEditor } from './RichTextEditor';
import { ImageManager } from './ImageManager';
import { AttributesEditor } from './AttributesEditor';
import { TranslationsEditor } from './TranslationsEditor';
import { NameTranslationsEditor } from './NameTranslationsEditor';
import { Product, Category } from '@/types';
import { generateSlug, isSlugUnique } from '@/lib/admin/products';
import { getAllCategoriesAdmin } from '@/lib/admin/categories';

interface ProductFormProps {
  product?: Product;
  onSubmit: (data: Partial<Product>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export function ProductForm({ product, onSubmit, onCancel, loading }: ProductFormProps) {
  const [slugError, setSlugError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [images, setImages] = useState<string[]>(product?.images || ['']);
  const [thumbnailIndex, setThumbnailIndex] = useState<number | undefined>(
    product?.images && product.images.length > 0 ? 0 : undefined
  );
  const [attributes, setAttributes] = useState<Record<string, { label: string; value: string; unit?: string }>>(
    product?.attributes || {}
  );
  const [translations, setTranslations] = useState(() => {
    if (product?.translations) {
      return product.translations;
    }
    return {
      pl: {
        shortDescription: product?.shortDescription || '',
        description: product?.description || '',
        applications: [],
        documents: [],
        tests: [],
      },
      en: {
        shortDescription: product?.shortDescription || '',
        description: product?.description || '',
        applications: [],
        documents: [],
        tests: [],
      },
    };
  });
  const [nameTranslations, setNameTranslations] = useState<Record<string, string>>(() => {
    const translations = product?.nameTranslations || {};
    // Filter out undefined and null values
    const filtered: Record<string, string> = {};
    Object.entries(translations).forEach(([key, val]) => {
      if (val !== undefined && val !== null && typeof val === 'string') {
        filtered[key] = val;
      }
    });
    return filtered;
  });

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getAllCategoriesAdmin();
        // Only show active categories
        setCategories(data.filter(c => c.isActive));
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Update translations when product changes
  useEffect(() => {
    if (product) {
      setAttributes(product.attributes || {});
      setTranslations(product.translations || {
        pl: {
          shortDescription: product.shortDescription || '',
          description: product.description || '',
          applications: [],
          documents: [],
          tests: [],
        },
        en: {
          shortDescription: product.shortDescription || '',
          description: product.description || '',
          applications: [],
          documents: [],
          tests: [],
        },
      });
      const translations = product.nameTranslations || {};
      // Filter out undefined and null values
      const filtered: Record<string, string> = {};
      Object.entries(translations).forEach(([key, val]) => {
        if (val !== undefined && val !== null && typeof val === 'string') {
          filtered[key] = val;
        }
      });
      setNameTranslations(filtered);
    }
  }, [product]);

  // Helper function to check if HTML has actual text content
  const hasTextContent = (html: string): boolean => {
    if (!html || html.trim().length === 0) return false;
    // Check if we're in a browser environment
    if (typeof document === 'undefined') {
      // Fallback: check if HTML contains any text nodes (basic check)
      return html.replace(/<[^>]*>/g, '').trim().length > 0;
    }
    // Create a temporary DOM element to extract text content
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.trim().length > 0;
  };

  const form = useForm({
    initialValues: {
      name: product?.name || '',
      slug: product?.slug || '',
      price: product?.price || 0,
      shortDescription: product?.shortDescription || '',
      description: product?.description || '',
      category: product?.category || '',
      inStock: product?.inStock ?? true,
      stockQuantity: product?.stockQuantity || 0,
    },
    validate: {
      name: (value) => (value.length < 1 ? 'Name is required' : null),
      slug: (value) => (value.length < 1 ? 'Slug is required' : null),
      price: (value) => (value <= 0 ? 'Price must be greater than 0' : null),
      shortDescription: (value) => (value.length < 1 ? 'Short description is required' : null),
      description: (value) => (!hasTextContent(value) ? 'Description is required' : null),
    },
  });

  // Auto-generate slug from name
  useEffect(() => {
    if (!product && form.values.name) {
      const generatedSlug = generateSlug(form.values.name);
      form.setFieldValue('slug', generatedSlug);
    }
  }, [form.values.name, product]);

  // Validate slug uniqueness
  const validateSlug = async (slug: string) => {
    if (!slug) {
      setSlugError(null);
      return;
    }

    const unique = await isSlugUnique(slug, product?.id);
    if (!unique) {
      setSlugError('This slug is already in use');
    } else {
      setSlugError(null);
    }
  };

  useEffect(() => {
    if (form.values.slug) {
      validateSlug(form.values.slug);
    }
  }, [form.values.slug]);

  const handleSubmit = async (values: typeof form.values) => {
    // Check slug validation
    if (slugError) {
      notifications.show({
        title: 'Validation Error',
        message: slugError,
        color: 'red',
      });
      return;
    }

    // Validate slug uniqueness before submitting (in case validation is still pending)
    if (form.values.slug) {
      const unique = await isSlugUnique(form.values.slug, product?.id);
      if (!unique) {
        setSlugError('This slug is already in use');
        notifications.show({
          title: 'Validation Error',
          message: 'This slug is already in use. Please choose a different slug.',
          color: 'red',
        });
        return;
      }
    }

    // Filter out empty image URLs
    const validImages = images.filter((img) => img.trim() !== '');

    if (validImages.length === 0) {
      notifications.show({
        title: 'Validation Error',
        message: 'At least one image is required',
        color: 'red',
      });
      return;
    }

    // Convert nameTranslations to Product type format (ensuring pl is present if any translations exist)
    const finalNameTranslations = Object.keys(nameTranslations).length > 0 
      ? (nameTranslations as Product['nameTranslations'])
      : undefined;

    const productData: Partial<Product> = {
      ...values,
      images: validImages,
      attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
      translations,
      nameTranslations: finalNameTranslations,
    };

    try {
      await onSubmit(productData);
    } catch (error: any) {
      // Error handling is done in the parent component, but log here for debugging
      console.error('Error submitting product form:', error);
      // Re-throw to let parent handle it
      throw error;
    }
  };


  return (
    <Container size="xl">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Title order={2}>{product ? 'Edit Product' : 'Create New Product'}</Title>

          <TextInput
            label="Product Name"
            placeholder="Enter product name"
            required
            {...form.getInputProps('name')}
          />

          <TextInput
            label="Slug"
            placeholder="product-slug"
            required
            error={slugError || form.errors.slug}
            {...form.getInputProps('slug')}
            description="URL-friendly identifier (auto-generated from name)"
          />

          <Group grow>
            <NumberInput
              label="Price (PLN brutto)"
              description="Gross price including 23% VAT"
              placeholder="0.00"
              required
              min={0}
              step={0.01}
              decimalScale={2}
              fixedDecimalScale
              {...form.getInputProps('price')}
            />

            <Select
              label="Category"
              placeholder="Select category"
              data={categories.map(c => ({ value: c.slug, label: c.name }))}
              disabled={categoriesLoading}
              searchable
              clearable
              {...form.getInputProps('category')}
            />
          </Group>

          <ImageManager
            images={images}
            onChange={setImages}
            thumbnailIndex={thumbnailIndex}
            onThumbnailChange={setThumbnailIndex}
          />

          <Tabs defaultValue="basic">
            <Tabs.List>
              <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
              <Tabs.Tab value="attributes">Attributes</Tabs.Tab>
              <Tabs.Tab value="translations">Translations</Tabs.Tab>
              <Tabs.Tab value="nameTranslations">Name Translations</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic" pt="md">
              <Stack gap="md">
                <Textarea
                  label="Short Description"
                  placeholder="Brief product description"
                  required
                  rows={3}
                  {...form.getInputProps('shortDescription')}
                />

                <RichTextEditor
                  label="Description"
                  content={form.values.description}
                  onChange={(content) => form.setFieldValue('description', content)}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="attributes" pt="md">
              <AttributesEditor
                attributes={attributes}
                onChange={setAttributes}
              />
            </Tabs.Panel>

            <Tabs.Panel value="translations" pt="md">
              <TranslationsEditor
                translations={translations}
                onChange={setTranslations}
              />
            </Tabs.Panel>

            <Tabs.Panel value="nameTranslations" pt="md">
              <NameTranslationsEditor
                nameTranslations={nameTranslations}
                onChange={setNameTranslations}
              />
            </Tabs.Panel>
          </Tabs>

          <Group grow>
            <Switch
              label="In Stock"
              {...form.getInputProps('inStock', { type: 'checkbox' })}
            />

            <NumberInput
              label="Stock Quantity"
              placeholder="0"
              min={0}
              {...form.getInputProps('stockQuantity')}
            />
          </Group>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              {product ? 'Update Product' : 'Create Product'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}

